import algoliasearch from 'algoliasearch'
import defaults, { metersPerMile } from '@config/defaults'
import { centsToDollarsFormatted } from '@lib/conversions'
import { isBrowser } from '@utils/environment'
import {
  EMPTY_RESULTS,
  getAlgoliaPersonaId,
  PGA_COACH_GROUP_EVENT_OFFERING_FORMAT_FACET_TAGS,
  PGA_COACH_PERSONA_ALIAS_MAP,
  PGA_COACH_PERSONA_MAP,
} from './constants'
import {
  getJourneyDistanceMeters,
  getJourneyPriceOption,
  normalizeJourneySearchState,
} from './search-state'
import {
  NOTIFY_ME_LABEL,
  resolveAcceptsRegistrationOpenNotifications,
  showRegistrationOpenNotification,
} from './group-event-registration'

const searchClient = algoliasearch(
  process.env.ALGOLIA_APP_ID,
  process.env.ALGOLIA_API_KEY,
)

const pickFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== '')

const parseBoolean = (value) => value === true || value === 'true'

const parseArray = (value) => {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  return [value]
}

const parseNumber = (value) => {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

const parseDate = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const COACH_PROFILE_OFFERING_DATE_FIELDS = [
  'nextAvailableAt',
  'startDateTime',
  'endDateTime',
  'updatedAt',
]

/** Re-parse date fields after JSON serialization through getInitialProps. */
export const reviveCoachProfileOffering = (offering) => {
  if (!offering || typeof offering !== 'object') return offering
  const revived = { ...offering }
  COACH_PROFILE_OFFERING_DATE_FIELDS.forEach((field) => {
    if (revived[field] != null) {
      revived[field] = parseDate(revived[field])
    }
  })
  return revived
}

export const reviveCoachProfileOfferingsByPersona = (offeringsByPersona) => {
  if (!offeringsByPersona || typeof offeringsByPersona !== 'object') {
    return offeringsByPersona
  }
  return Object.fromEntries(
    Object.entries(offeringsByPersona).map(([personaId, offerings]) => [
      personaId,
      (offerings || []).map(reviveCoachProfileOffering),
    ]),
  )
}

const parseTimezone = (value) => {
  if (!value) return null
  try {
    Intl.DateTimeFormat(undefined, { timeZone: value })
    return value
  } catch {
    return null
  }
}

const parseCoordinates = (value) => {
  if (!value) return null
  const lat = Number(value.lat)
  const lng = Number(value.lng)
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null
  return { lat, lng }
}

export const buildGroupEventsSearchFilters = (
  currentTimeSeconds = new Date().getTime() / 1000,
  { includeFullEvents = false } = {},
) => {
  const filters = (
    '(registration_closed:false OR registration_closed:null) '
    + `AND (start_date_time_ts > ${currentTimeSeconds} OR end_date_time_ts > ${currentTimeSeconds}) `
    + `AND registration_close_date_time_ts > ${currentTimeSeconds} `
    + 'AND is_canceled:false'
  )
  if (includeFullEvents) return filters
  return `${filters} AND registration_full:false`
}

// `has_waitlist` is indexed on GROUP_EVENTS but is not a filterable facet, so
// waitlist-full events cannot be selected in Algolia. Journey and coach-profile
// queries pass `includeFullEvents` and keep full+waitlist hits after normalize.
export const isGroupEventEligibleForJourneySearch = (offering) => (
  !offering?.registrationFull || Boolean(offering?.hasWaitlist)
)

/**
 * Algolia `tags` clause for Group vs Program on the mixed GROUP_EVENTS index.
 * Program = PGA Jr. League only. Both or neither selected → no extra tag filter.
 */
export const buildGroupEventsFormatTagFilter = (formats = []) => {
  const selected = parseArray(formats)
  const wantsGroup = selected.includes('group')
  const wantsProgram = selected.includes('program')
  if (wantsGroup === wantsProgram) return null
  if (wantsGroup) return '(NOT tags:"PGA Jr. League")'
  return 'tags:"PGA Jr. League"'
}

export const buildPrivateOfferingsSearchFilters = (privateCategories = []) => {
  if (!privateCategories.length) return null
  const clauses = privateCategories.map((cat) => `category:${cat}`)
  return clauses.length === 1 ? clauses[0] : `(${clauses.join(' OR ')})`
}

/**
 * `coach_id` on PRIVATE_OFFERINGS is numeric, not a facet.
 * Facet syntax (`coach_id:123`) matches 0 hits on prod/dev private indexes;
 * numeric comparison (`coach_id = 123`) returns the lessons.
 * GROUP_EVENTS still uses facet syntax (`coach_id:123`).
 */
export const buildPrivateOfferingsCoachIdFilter = (coachId) => `coach_id = ${coachId}`

export const buildPersonaFilter = (appPersonaId) => {
  if (!appPersonaId) return null
  return `personas:${getAlgoliaPersonaId(appPersonaId)}`
}

export const buildAvailabilityWindowFilter = (scheduleId) => {
  if (!scheduleId || scheduleId === 'any') return null
  return `availability_windows:${scheduleId}`
}

export const buildGroupEventsTimeWindowFilter = (scheduleId, now = new Date()) => {
  if (!scheduleId || scheduleId === 'any') return null
  const toSeconds = (date) => Math.floor(date.getTime() / 1000)

  const endOfThisWeek = new Date(now)
  endOfThisWeek.setDate(now.getDate() + (6 - now.getDay()))
  endOfThisWeek.setHours(23, 59, 59, 999)

  if (scheduleId === 'this_week') {
    return `start_date_time_ts <= ${toSeconds(endOfThisWeek)}`
  }
  if (scheduleId === 'next_week') {
    const startOfNextWeek = new Date(endOfThisWeek)
    startOfNextWeek.setDate(endOfThisWeek.getDate() + 1)
    startOfNextWeek.setHours(0, 0, 0, 0)
    const endOfNextWeek = new Date(startOfNextWeek)
    endOfNextWeek.setDate(startOfNextWeek.getDate() + 6)
    endOfNextWeek.setHours(23, 59, 59, 999)
    return `start_date_time_ts >= ${toSeconds(startOfNextWeek)} AND start_date_time_ts <= ${toSeconds(endOfNextWeek)}`
  }
  if (scheduleId === 'this_month') {
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
    return `start_date_time_ts <= ${toSeconds(endOfMonth)}`
  }
  if (scheduleId === 'next_month') {
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0)
    const endOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999)
    return `start_date_time_ts >= ${toSeconds(startOfNextMonth)} AND start_date_time_ts <= ${toSeconds(endOfNextMonth)}`
  }
  return null
}

export const buildPriceFilter = (priceId) => {
  if (!priceId || priceId === 'all') return null
  const option = getJourneyPriceOption(priceId)
  if (!option || option.id === 'all') return null
  if (option.id === 'free') return 'price_in_cents = 0'
  const clauses = []
  if (option.minExclusive != null) clauses.push(`price_in_cents > ${option.minExclusive * 100}`)
  if (option.min != null) clauses.push(`price_in_cents >= ${option.min * 100}`)
  if (option.max != null) clauses.push(`price_in_cents < ${option.max * 100}`)
  return clauses.length > 0 ? clauses.join(' AND ') : null
}

const normalizeCoach = (hit) => ({
  name: hit.coach_name,
  slug: hit.coach_slug,
  credentials: pickFirstDefined(hit.certification_level, hit.certificationLevel),
  coachTitle: pickFirstDefined(hit.coach_title, hit.coachTitle),
  bio: pickFirstDefined(hit.coach_bio, hit.bio, hit.coachBio, hit.coach?.bio),
  memberType: pickFirstDefined(hit.member_type, hit.memberType),
  certificationLevel: pickFirstDefined(hit.certification_level, hit.certificationLevel),
  photoUrl: pickFirstDefined(
    hit.coach_profile_photo,
    hit.coach?.photoUrl,
    hit.coach?.photo_url,
    hit.coach_photo_url,
    defaults.coachPlaceholder,
  ),
  profileUrl: null,
  otherOfferingsCount: 0,
})

/** @param {Record<string, unknown>} hit Algolia-ish hit **after** {@link normalizeHitPersonas}. */
export const buildJourneyOfferingCardTagLabels = (hit = {}) => {
  const personaLabels = parseArray(hit.personas)
    .map((personaAppId) => PGA_COACH_PERSONA_MAP[personaAppId]?.label)
    .filter(Boolean)
  const descriptiveTags = parseArray(hit.tags).filter((tag) => (
    !PGA_COACH_GROUP_EVENT_OFFERING_FORMAT_FACET_TAGS.has(String(tag).trim().toLowerCase())
  ))
  const { list: merged } = [...personaLabels, ...descriptiveTags].reduce(
    (acc, label) => {
      const trimmed = String(label).trim()
      if (!trimmed) return acc
      const dedupeKey = trimmed.toLowerCase()
      if (acc.seen.has(dedupeKey)) return acc
      acc.seen.add(dedupeKey)
      acc.list.push(trimmed)
      return acc
    },
    /** @type {{ seen: Set<string>, list: string[] }} */ ({
      seen: new Set(),
      list: [],
    }),
  )
  return merged.slice(0, 4)
}

const getMinimumLessonPackPricePerLessonInCents = (lessonPackOfferings) => {
  const packs = parseArray(lessonPackOfferings)
  if (!packs.length) return null
  const perLessonPrices = packs
    .map((pack) => {
      const priceInCents = parseNumber(pickFirstDefined(pack.price_in_cents, pack.priceInCents))
      const quantity = parseNumber(pack.quantity)
      if (priceInCents == null || !quantity) return null
      return priceInCents / quantity
    })
    .filter((price) => price != null)
  if (!perLessonPrices.length) return null
  return Math.min(...perLessonPrices)
}

export const normalizeOfferingHit = (hit = {}) => {
  const coach = normalizeCoach(hit)
  const isJrl = parseArray(hit.tags).some((tag) => String(tag).trim().toLowerCase() === 'pga jr. league')
  const inferredFormat = isJrl ? 'program' : 'group'
  const coordinates = parseCoordinates(pickFirstDefined(
    hit._geoloc,
    hit.location?._geoloc,
    hit.coach?._geoloc,
  ))
  const nextAvailableAt = parseDate(pickFirstDefined(
    hit.nextAvailableAt,
    hit.next_available_at,
    hit.start_date_time,
    hit.startDateTime,
  ))
  const basePriceInCents = parseNumber(pickFirstDefined(
    hit.startingPriceInCents,
    hit.priceInCents,
    hit.price_in_cents,
    hit.total_price_in_cents,
  ))
  const additionalFeesInCents = isJrl ? (parseNumber(hit.additional_fees) || 0) : 0
  let priceInCents = basePriceInCents != null ? basePriceInCents + additionalFeesInCents : null
  const lessonPackOfferings = parseArray(
    pickFirstDefined(hit.lesson_pack_offerings, hit.lessonPackOfferings),
  )
  const minLessonPackPricePerLessonInCents = getMinimumLessonPackPricePerLessonInCents(
    lessonPackOfferings,
  )
  let priceLabel = hit.priceLabel || null
  if (lessonPackOfferings.length > 0 && minLessonPackPricePerLessonInCents != null) {
    priceInCents = Math.round(minLessonPackPricePerLessonInCents)
    if (!priceLabel) {
      const formattedPrice = centsToDollarsFormatted(priceInCents)
      priceLabel = formattedPrice ? `Starting at ${formattedPrice}` : null
    }
  }
  const durationInMinutes = parseNumber(pickFirstDefined(
    hit.durationInMinutes,
    hit.duration_in_minutes,
  ))
  const format = pickFirstDefined(hit.format, hit.offeringFormat, hit.offering_type, hit.type, inferredFormat)
  const primaryFacility = hit.facilities?.[0]
  const city = pickFirstDefined(
    hit.coach_facility_city,
    hit.primary_facility_city,
    primaryFacility?.facility_city,
    hit.city,
    hit.location?.city,
    hit.coach?.city,
  )
  const state = pickFirstDefined(
    hit.coach_facility_state,
    hit.primary_facility_state,
    primaryFacility?.facility_state,
    hit.state,
    hit.location?.state,
    hit.coach?.state,
  )
  const venueName = pickFirstDefined(
    hit.venueName,
    hit.facilityName,
    hit.locationName,
    hit.coach_facility_name,
    hit.primary_facility_name,
    primaryFacility?.facility_name,
  )
  const personaTags = parseArray(pickFirstDefined(
    hit.personaTags,
    hit.personas,
    hit.audiences,
    hit.tags,
  ))
  const tagLabels = buildJourneyOfferingCardTagLabels(hit)
  const hasAvailability = parseBoolean(pickFirstDefined(
    hit.hasAvailability,
    hit.isBookable,
    hit.bookable,
  )) || Boolean(nextAvailableAt)
  const registrationFull = parseBoolean(pickFirstDefined(hit.registrationFull, hit.registration_full))
  const hasWaitlist = parseBoolean(pickFirstDefined(hit.hasWaitlist, hit.has_waitlist))
  const timezone = parseTimezone(pickFirstDefined(
    hit.coach_facility_timezone,
    hit.coachFacilityTimezone,
    hit.timezone,
  ))
  const registrationOpenDateTime = pickFirstDefined(
    hit.registrationOpenDateTime,
    hit.registration_open_date_time,
  ) || null
  const externalRegistrationUrl = pickFirstDefined(
    hit.externalRegistrationUrl,
    hit.external_registration_url,
  ) || null
  const isCanceled = parseBoolean(pickFirstDefined(hit.isCanceled, hit.is_canceled))
  const registrationOpenEvent = {
    registrationOpenDateTime,
    timezone,
    externalRegistrationUrl,
    isCanceled,
    acceptsRegistrationOpenNotifications: resolveAcceptsRegistrationOpenNotifications(
      pickFirstDefined(
        hit.acceptsRegistrationOpenNotifications,
        hit.accepts_registration_open_notifications,
      ),
      { registrationOpenDateTime, timezone, isCanceled },
    ),
  }
  const ctaType = pickFirstDefined(hit.ctaType, hit.actionType, format === 'group' || format === 'program' ? 'register' : 'pick-date')
  let defaultCtaLabel = 'Book Now'
  if (ctaType === 'register') defaultCtaLabel = 'Register'
  if (registrationFull && hasWaitlist) defaultCtaLabel = 'Join Waitlist'
  if (showRegistrationOpenNotification(registrationOpenEvent)) defaultCtaLabel = NOTIFY_ME_LABEL
  const ctaLabel = pickFirstDefined(hit.ctaLabel, defaultCtaLabel)
  const jrlBaseUrl = (process.env.JRL_MARKETING_SITE_URL || '').replace(/\/$/, '')
  const ctaUrl = pickFirstDefined(
    hit.ctaUrl,
    hit.bookingUrl,
    hit.url,
    isJrl && hit.objectID && jrlBaseUrl ? `${jrlBaseUrl}/junior-league-events/${hit.objectID}` : null,
    !isJrl && hit.slug ? `/things-to-do/events/${hit.slug}` : null,
  )
  const profileUrl = coach.profileUrl || (coach.slug ? `/coach/${coach.slug}` : null)
  const geoDistanceMeters = parseNumber(pickFirstDefined(
    hit._rankingInfo?.matchedGeoLocation?.distance,
    hit._rankingInfo?.geoDistance,
  ))
  const maxAttendance = parseNumber(pickFirstDefined(hit.maxAttendance, hit.max_attendance))
  const registrationCount = parseNumber(pickFirstDefined(hit.registrationCount, hit.registration_count))
  return {
    objectID: pickFirstDefined(hit.objectID, hit.id, hit.slug, hit.title),
    lessonTypeId: pickFirstDefined(hit.objectID, hit.id),
    slug: hit.slug || null,
    lessonCount: parseNumber(pickFirstDefined(hit.lessonCount, hit.lesson_count)),
    title: pickFirstDefined(hit.title, hit.name, 'Offering'),
    description: pickFirstDefined(hit.description, hit.summary, hit.shortDescription, ''),
    format,
    personaTags,
    tagLabels,
    priceInCents,
    durationInMinutes,
    priceLabel,
    nextAvailableAt,
    venueName,
    coachFacilityFullAddress: pickFirstDefined(
      hit.coachFacilityFullAddress,
      hit.coach_facility_full_address,
      primaryFacility?.facility_full_address,
      hit.primary_facility_full_address,
    ),
    city,
    state,
    maxAttendance,
    registrationCount,
    registrationFull,
    hasWaitlist,
    registrationOpenDateTime,
    acceptsRegistrationOpenNotifications: registrationOpenEvent.acceptsRegistrationOpenNotifications,
    externalRegistrationUrl,
    isCanceled,
    startDateTime: hit.start_date_time ? new Date(hit.start_date_time) : null,
    endDateTime: hit.end_date_time ? new Date(hit.end_date_time) : null,
    updatedAt: parseDate(pickFirstDefined(hit.updated_at, hit.updatedAt)),
    timezone,
    coach,
    ctaType,
    ctaLabel,
    ctaUrl,
    profileUrl,
    hasAvailability,
    coachPlusBoost: parseBoolean(pickFirstDefined(
      hit.hasPlusSubscription,
      hit.has_plus_subscription,
      hit.coach?.hasPlusSubscription,
    )),
    geoDistanceMeters,
    coordinates,
    imageUrl: pickFirstDefined(
      hit.featured_image_url,
      hit.imageUrl,
      hit.featuredImageUrl,
      hit.image?.url,
    ),
    availabilityWindows: parseArray(pickFirstDefined(hit.availability_windows, hit.availabilityWindows)),
    category: pickFirstDefined(hit.category, hit.offeringCategory),
    facilityId: pickFirstDefined(
      hit.coach_facility_id,
      hit.coachFacilityId,
      hit.facility_id,
      hit.facilityId,
      primaryFacility?.facility_id,
      primaryFacility?.facilityId,
    ),
    hasLessonPacks: lessonPackOfferings.length > 0,
    requiresAccessCode: parseBoolean(pickFirstDefined(
      hit.requiresAccessCode,
      hit.requires_access_code,
      hit.accessCodeRequired,
      hit.access_code_required,
    )),
    rawHit: hit,
  }
}

const hasLessonPackOfferings = (hit) => parseArray(
  hit?.lesson_pack_offerings ?? hit?.lessonPackOfferings,
).length > 0

const buildPrivateBookingUrl = (coachSlug, lessonTypeId, facilityId, hasLessonPacks = false) => {
  if (!coachSlug || !lessonTypeId) return null
  const base = (process.env.MY_PGA_COM_URL || '').replace(/\/$/, '')
  if (!base) return null
  let url = `${base}/coach/${coachSlug}/schedule?lessonTypeId=${lessonTypeId}`
  if (facilityId) url += `&locationId=${facilityId}`
  if (hasLessonPacks) url += '&isLessonPacks=true'
  return url
}

const expandByFacilities = (offering) => {
  const facilities = parseArray(offering.rawHit?.facilities)
  const isPrivate = offering.format === 'private'
  const hasLessonPacks = hasLessonPackOfferings(offering.rawHit)
  // eslint-disable-next-line no-unused-vars
  const { rawHit, ...offeringWithoutRaw } = offering

  if (facilities.length <= 1) {
    const f = facilities[0]
    if (!f) {
      const ctaUrl = isPrivate
        ? buildPrivateBookingUrl(
          offering.coach?.slug,
          offering.lessonTypeId,
          null,
          hasLessonPacks,
        ) || offering.ctaUrl
        : offering.ctaUrl
      return [{ ...offeringWithoutRaw, ctaUrl }]
    }
    return [{
      ...offeringWithoutRaw,
      venueName: pickFirstDefined(offering.venueName, f.facility_name),
      city: pickFirstDefined(offering.city, f.facility_city),
      state: pickFirstDefined(offering.state, f.facility_state),
      coachFacilityFullAddress: pickFirstDefined(offering.coachFacilityFullAddress, f.facility_full_address),
      coordinates: offering.coordinates || parseCoordinates({ lat: f.lat, lng: f.lng }),
      facilityId: f.facility_id || f.facilityId || offering.facilityId,
      ctaUrl: isPrivate
        ? buildPrivateBookingUrl(
          offering.coach?.slug,
          offering.lessonTypeId,
          f.facility_id,
          hasLessonPacks,
        ) || offering.ctaUrl
        : offering.ctaUrl,
    }]
  }
  return facilities.map((f, index) => ({
    ...offeringWithoutRaw,
    objectID: `${offering.objectID}::${f.facility_id || index}`,
    venueName: f.facility_name || offering.venueName,
    city: f.facility_city || offering.city,
    state: f.facility_state || offering.state,
    coachFacilityFullAddress: f.facility_full_address || offering.coachFacilityFullAddress,
    coordinates: parseCoordinates({ lat: f.lat, lng: f.lng }) || offering.coordinates,
    facilityId: f.facility_id || f.facilityId || offering.facilityId,
    ctaUrl: isPrivate
      ? buildPrivateBookingUrl(
        offering.coach?.slug,
        offering.lessonTypeId,
        f.facility_id,
        hasLessonPacks,
      ) || offering.ctaUrl
      : offering.ctaUrl,
  }))
}

const isWithinPriceRange = (offering, priceSelection) => {
  if (priceSelection === 'all') return true
  const option = getJourneyPriceOption(priceSelection)
  if (offering.priceInCents == null) return false
  const priceInDollars = offering.priceInCents / 100
  if (option.id === 'free') return priceInDollars === 0
  if (option.minExclusive != null && priceInDollars <= option.minExclusive) return false
  if (option.min != null && priceInDollars < option.min) return false
  if (option.max != null && priceInDollars >= option.max) return false
  return true
}

/** Schedule filtering is handled at the Algolia query level; client-side is a pass-through. */
export const matchesScheduleFilter = () => true

const COACH_PROFILE_AVAILABILITY_WINDOW_PRIORITY = [
  'this_week',
  'next_week',
  'this_month',
  'next_month',
]

/**
 * Lower rank = sooner availability. Used to sort coach-profile persona-tab
 * offering cards so this-week / next-week slots surface first.
 */
export const getOfferingAvailabilityUrgencyRank = (offering) => {
  const windows = offering?.availabilityWindows || []
  const windowIndex = COACH_PROFILE_AVAILABILITY_WINDOW_PRIORITY.findIndex(
    (windowId) => windows.includes(windowId),
  )
  if (windowIndex >= 0) return windowIndex
  if (offering?.hasAvailability) return COACH_PROFILE_AVAILABILITY_WINDOW_PRIORITY.length
  if (offering?.nextAvailableAt) return COACH_PROFILE_AVAILABILITY_WINDOW_PRIORITY.length + 1
  return Number.POSITIVE_INFINITY
}

export const hasImminentCoachProfileAvailability = (offering) => (
  getOfferingAvailabilityUrgencyRank(offering) <= 1
)

export const getSoonestAvailabilityWindow = (offering) => {
  const windows = offering?.availabilityWindows || []
  return COACH_PROFILE_AVAILABILITY_WINDOW_PRIORITY.find((windowId) => windows.includes(windowId))
}

const getOfferingNextAvailableTime = (offering) => {
  const parsed = parseDate(offering?.nextAvailableAt)
  return parsed ? parsed.getTime() : Number.POSITIVE_INFINITY
}

export const sortCoachProfileOfferingsByAvailability = (offerings = []) => (
  [...offerings].sort((left, right) => {
    const rankDiff = getOfferingAvailabilityUrgencyRank(left)
      - getOfferingAvailabilityUrgencyRank(right)
    if (rankDiff !== 0) return rankDiff
    const leftNext = getOfferingNextAvailableTime(left)
    const rightNext = getOfferingNextAvailableTime(right)
    if (leftNext !== rightNext) return leftNext - rightNext
    return (left.title || '').localeCompare(right.title || '')
  })
)

const LESSON_PACKAGE_FORMATS = new Set([
  'package',
  'bundle',
  'lesson_pack',
  'lesson-package',
  'lesson_package',
  'lessonpack',
])

/** Salon-style service buckets on the coach profile offerings shop.
 * `iconKey` maps to `COACH_PROFILE_OFFERING_FORMAT_ICON_COMPONENTS` (gift on packages,
 * lock on access-code). */
export const COACH_PROFILE_OFFERING_SERVICES = [
  { id: 'private', label: 'Private lessons', iconKey: 'private' },
  { id: 'group', label: 'Group programs', iconKey: 'group' },
  { id: 'jrl', label: 'PGA Jr. League', iconKey: 'program' },
  { id: 'packages', label: 'Lesson Packages', iconKey: 'packages' },
  { id: 'access-code', label: 'Access Code Required', iconKey: 'access-code' },
]

/** Not a persona id. Holds offerings Algolia sent with empty `personas`. */
export const COACH_PROFILE_UNTAGGED_OFFERINGS_KEY = '_untagged'

const isJrLeagueOffering = (offering) => {
  if (!offering) return false
  if (offering.format === 'program') return true
  const labels = parseArray(offering.tagLabels)
  return labels.some((tag) => String(tag).trim().toLowerCase() === 'pga jr. league')
}

const isLessonPackageFormat = (offering) => (
  LESSON_PACKAGE_FORMATS.has(String(offering?.format || '').trim().toLowerCase())
)

const offeringHasLessonPackages = (offering) => (
  Boolean(offering?.hasLessonPacks) || isLessonPackageFormat(offering)
)

/** Exclusive primary bucket for All-view section grouping. */
export const getCoachProfileOfferingServiceId = (offering) => {
  if (offering?.format === 'private') return 'private'
  if (isJrLeagueOffering(offering)) return 'jrl'
  if (isLessonPackageFormat(offering)) return 'packages'
  return 'group'
}

/** Chip filters may overlap (packs on a private lesson, access-code on a clinic). */
export const offeringMatchesShopFilter = (offering, filterId) => {
  if (!filterId || filterId === 'all') return true
  if (filterId === 'packages') return offeringHasLessonPackages(offering)
  if (filterId === 'access-code') return Boolean(offering?.requiresAccessCode)
  return getCoachProfileOfferingServiceId(offering) === filterId
}

/** Listing and facility-filter label: "Facility Name, City, ST". */
export const formatCoachProfileFacilityLabel = (offering = {}) => {
  const venueName = String(offering?.venueName || '').trim()
  const cityState = [offering?.city, offering?.state]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ')
  const parts = [venueName, cityState].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

/** Stable id for a shop facility chip (`id:` when Algolia has one). */
export const getCoachProfileFacilityFilterKey = (offering = {}) => {
  const facilityId = String(offering?.facilityId || '').trim()
  if (facilityId) return `id:${facilityId}`
  const label = formatCoachProfileFacilityLabel(offering)
  if (!label) return null
  return `label:${label.toLowerCase()}`
}

export const offeringMatchesFacilityFilter = (offering, filterKey) => {
  let keys = []
  if (Array.isArray(filterKey)) {
    keys = filterKey.filter(Boolean)
  } else if (filterKey && filterKey !== 'all') {
    keys = [filterKey]
  }
  if (!keys.length) return true
  const offeringKey = getCoachProfileFacilityFilterKey(offering)
  return keys.includes(offeringKey)
}

/** Location / Tags menu row: "Label (3)". */
const formatCountedFilterOptionLabel = (option = {}) => {
  const label = String(option.label || '').trim()
  if (!label) return ''
  const count = Number(option.count)
  if (!Number.isFinite(count) || count < 1) return label
  return `${label} (${count})`
}

/** Location menu row: "Facility Name, City, ST (3)". */
export const formatCoachProfileFacilityFilterOptionLabel = formatCountedFilterOptionLabel

/** Same title-case as listing-card chips. */
export const formatCoachProfileTagLabel = (value = '') => (
  String(value)
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ')
)

export const getCoachProfileOfferingTagIds = (offering = {}) => {
  const seen = new Set()
  const ids = []
  parseArray(offering.tagLabels).forEach((tag) => {
    const label = formatCoachProfileTagLabel(tag)
    const id = label.toLowerCase()
    if (!label || seen.has(id)) return
    seen.add(id)
    ids.push(id)
  })
  return ids
}

export const offeringMatchesTagFilter = (offering, filterKey) => {
  let keys = []
  if (Array.isArray(filterKey)) {
    keys = filterKey.filter(Boolean)
  } else if (filterKey && filterKey !== 'all') {
    keys = [filterKey]
  }
  if (!keys.length) return true
  const offeringIds = getCoachProfileOfferingTagIds(offering)
  return keys.some((key) => offeringIds.includes(key))
}

/** Tags menu row: "On Course (3)". */
export const formatCoachProfileTagFilterOptionLabel = formatCountedFilterOptionLabel

/**
 * Unique listing tags on this shop, hide-when-empty. Sorted by label.
 * `count` is how many offerings use that tag. Mount the Tags menu
 * only when this returns 2+.
 */
export const buildCoachProfileTagFilterOptions = (offerings = []) => {
  const byId = new Map()
  parseArray(offerings).forEach((offering) => {
    const seenOnOffering = new Set()
    parseArray(offering.tagLabels).forEach((tag) => {
      const label = formatCoachProfileTagLabel(tag)
      const id = label.toLowerCase()
      if (!label || seenOnOffering.has(id)) return
      seenOnOffering.add(id)
      const existing = byId.get(id)
      if (!existing) {
        byId.set(id, { id, label, count: 1 })
        return
      }
      existing.count += 1
    })
  })
  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Unique facilities on this shop, hide-when-empty. Sorted by label.
 * `count` is how many offerings sit at that venue. Mount the Location
 * menu only when this returns 2+.
 */
export const buildCoachProfileFacilityFilterOptions = (offerings = []) => {
  const byKey = new Map()
  parseArray(offerings).forEach((offering) => {
    const key = getCoachProfileFacilityFilterKey(offering)
    const label = formatCoachProfileFacilityLabel(offering)
    if (!key || !label) return
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, { id: key, label, count: 1 })
      return
    }
    existing.count += 1
    if (label.length > existing.label.length) {
      existing.label = label
    }
  })
  return [...byKey.values()].sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Persona maps duplicate the same Algolia hit. Collapse to one card per
 * `objectID` for the About-page shop. Includes the untagged bucket used when
 * Algolia sends empty `personas` (Jr. League often does).
 */
export const flattenUniqueCoachProfileOfferings = (offeringsByPersona = {}) => {
  const byId = new Map()
  Object.values(offeringsByPersona || {}).forEach((list) => {
    parseArray(list).forEach((offering) => {
      const key = String(offering?.objectID || '')
      if (!key || byId.has(key)) return
      byId.set(key, offering)
    })
  })
  return [...byId.values()]
}

/**
 * Persona-tab shop list. Juniors also includes untagged Jr. League hits so
 * empty-persona programs still show when golfers pick that audience.
 */
export const getCoachProfilePersonaShopOfferings = (offeringsByPersona, personaId) => {
  const personaList = parseArray(offeringsByPersona?.[personaId])
  if (personaId !== 'juniors') return personaList
  const extras = parseArray(offeringsByPersona?.[COACH_PROFILE_UNTAGGED_OFFERINGS_KEY])
    .filter((offering) => getCoachProfileOfferingServiceId(offering) === 'jrl')
  if (!extras.length) return personaList
  const byId = new Map();
  [...personaList, ...extras].forEach((offering) => {
    const key = String(offering?.objectID || '')
    if (!key || byId.has(key)) return
    byId.set(key, offering)
  })
  return [...byId.values()]
}

/** Lowest private-lesson price in cents, or null when none are priced. */
export const getCoachProfileStartingPriceInCents = (offerings = []) => {
  const prices = parseArray(offerings)
    .filter((offering) => (
      offering?.format === 'private'
      && Number.isFinite(offering.priceInCents)
      && offering.priceInCents > 0
    ))
    .map((offering) => offering.priceInCents)
  if (!prices.length) return null
  return Math.min(...prices)
}

const isMatchingFormats = (offering, formats = []) => {
  if (!formats.length) return true
  return formats.includes(offering.format)
}

const isMatchingPrivateCategories = (offering, privateCategories = []) => {
  if (offering.format !== 'private') return true
  if (!privateCategories.length) return true
  return Boolean(offering.category) && privateCategories.includes(offering.category)
}

const byRecommended = (left, right) => {
  if (left.hasAvailability !== right.hasAvailability) return Number(right.hasAvailability) - Number(left.hasAvailability)
  if (left.coachPlusBoost !== right.coachPlusBoost) return Number(right.coachPlusBoost) - Number(left.coachPlusBoost)
  if ((left.geoDistanceMeters || Infinity) !== (right.geoDistanceMeters || Infinity)) {
    return (left.geoDistanceMeters || Infinity) - (right.geoDistanceMeters || Infinity)
  }
  if ((left.priceInCents || Infinity) !== (right.priceInCents || Infinity)) {
    return (left.priceInCents || Infinity) - (right.priceInCents || Infinity)
  }
  return left.title.localeCompare(right.title)
}

export const sortOfferings = (offerings, sort) => {
  const items = [...offerings]
  switch (sort) {
  case 'recommended':
    return items.sort(byRecommended)
  case 'price-asc':
    return items.sort(
      (left, right) => (left.priceInCents || Infinity) - (right.priceInCents || Infinity),
    )
  case 'price-desc':
    return items.sort(
      (left, right) => (right.priceInCents || -Infinity) - (left.priceInCents || -Infinity),
    )
  default:
    return items.sort(byRecommended)
  }
}

const filterOfferings = (offerings, searchState) => offerings.filter((offering) => {
  const {
    formats,
    privateCategories,
    price,
    schedule,
  } = searchState
  if (!isMatchingFormats(offering, formats)) return false
  if (!isMatchingPrivateCategories(offering, parseArray(privateCategories))) return false
  if (!isWithinPriceRange(offering, price)) return false
  if (!matchesScheduleFilter(offering, schedule)) return false
  return true
})

export const normalizeHitPersonas = (hit = {}) => ({
  ...hit,
  personas: parseArray(hit.personas).map((p) => PGA_COACH_PERSONA_ALIAS_MAP[p] || p),
})

const normalizeEligibleGroupEventHits = (hits = []) => (
  (hits || [])
    .map(normalizeHitPersonas)
    .map(normalizeOfferingHit)
    .filter(isGroupEventEligibleForJourneySearch)
)

export const getCoachesIndex = () => searchClient.initIndex(process.env.ALGOLIA_COACH_INDEX_NAME)
export const JOURNEY_OFFERINGS_PAGE_SIZE = 12
const EXTERNALLY_BOOKABLE_COACHES_PAGE_SIZE = 20

export { detectJourneyLocationFromIp, detectLocationFromIp } from './detect-location-from-ip'

export const searchLocalCoaches = async ({ aroundLatLng, hitsPerPage = 3 } = {}) => {
  const searchParams = {
    hitsPerPage,
    attributesToRetrieve: [
      'displayName',
      'firstName',
      'lastName',
      'customSlug',
      'profilePhoto',
      'city',
      'state',
      'facilities',
      'bookable',
      'bookingURL',
      'hasIntroVideo',
      'introductionVideoUrl',
      'universalId',
      'memberType',
      'certificationLevel',
      'acceptsLeads',
      'freeTierLimitsReached',
      'hasPlusSubscription',
      'bio',
      'startingPrice',
      '_rankingInfo',
    ],
    getRankingInfo: true,
  }
  if (aroundLatLng) {
    searchParams.aroundLatLng = aroundLatLng
    searchParams.aroundRadius = 50 * metersPerMile
  } else {
    searchParams.aroundLatLngViaIP = true
  }
  const { hits } = await getCoachesIndex().search('', searchParams)
  return (hits || []).map((hit) => ({
    objectID: hit.objectID,
    displayName: hit.displayName,
    firstName: hit.firstName,
    lastName: hit.lastName,
    customSlug: hit.customSlug,
    profilePhoto: hit.profilePhoto,
    city: hit.city,
    state: hit.state,
    facilities: hit.facilities || [],
    bookingURL: hit.bookingURL,
    bookable: hit.bookable,
    hasIntroVideo: hit.hasIntroVideo,
    introductionVideoUrl: hit.introductionVideoUrl,
    universalId: hit.universalId,
    memberType: hit.memberType,
    certificationLevel: hit.certificationLevel,
    acceptsLeads: hit.acceptsLeads,
    freeTierLimitsReached: hit.freeTierLimitsReached,
    hasPlusSubscription: hit.hasPlusSubscription,
    bio: hit.bio,
    startingPrice: hit.startingPrice,
    _rankingInfo: hit._rankingInfo,
  }))
}

const COACH_ATTRIBUTES_TO_RETRIEVE = [
  'objectID',
  'displayName',
  'firstName',
  'lastName',
  'customSlug',
  'profilePhoto',
  'title',
  'city',
  'state',
  'facilities',
  'bookingURL',
  'isExternallyBookable',
  'memberType',
  'certificationLevel',
  'hasPlusSubscription',
  '_rankingInfo',
]

/** Algolia facet `objectID` filter: OR of quoted IDs (supports `::` compound private rows). */
const buildOfferingObjectIdsFilter = (objectIds = []) => {
  const ids = [...new Set(parseArray(objectIds).map(String).filter(Boolean))]
  if (!ids.length) return null
  return ids
    .map((id) => {
      const escaped = String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"')
      return `objectID:"${escaped}"`
    })
    .join(' OR ')
}

/**
 * Saved-offerings mode: resolves hearted Algolia ids only — ignores persona, geo radius,
 * price, formats, schedule, and text query. Order follows localStorage id order, then `sortOfferings`.
 */
const searchPgaCoachOfferingsSavedOnly = async (searchState = {}, normalizedState = {}) => {
  const requestedPage = parseNumber(searchState.page) || parseNumber(normalizedState.page) || 1
  const page = Math.max(requestedPage, 1)
  const perPage = parseNumber(searchState.perPage) || parseNumber(normalizedState.perPage) || JOURNEY_OFFERINGS_PAGE_SIZE
  const hitsPerPageCumulative = page * perPage
  const idsRaw = parseArray(
    normalizedState.savedOfferingIds ?? searchState.savedOfferingIds,
  ).map(String).filter(Boolean)
  const seenOrdered = new Set()
  const idsInSaveOrder = idsRaw.filter((id) => {
    if (seenOrdered.has(id)) return false
    seenOrdered.add(id)
    return true
  })

  const idFilter = buildOfferingObjectIdsFilter(idsInSaveOrder)
  if (!idFilter) {
    return {
      ...EMPTY_RESULTS,
      page,
      perPage,
      totalPages: 1,
      coachHits: [],
    }
  }

  const hitsPerRequested = Math.min(Math.max(idsInSaveOrder.length, hitsPerPageCumulative), 1000)

  const { results } = await searchClient.search([
    {
      indexName: process.env.ALGOLIA_GROUP_EVENTS_INDEX_NAME,
      query: '',
      params: {
        hitsPerPage: hitsPerRequested,
        filters: `(${idFilter})`,
      },
    },
    {
      indexName: process.env.ALGOLIA_PRIVATE_OFFERINGS_INDEX_NAME,
      query: '',
      params: {
        hitsPerPage: hitsPerRequested,
        filters: `(${idFilter})`,
      },
    },
  ])

  const groupEventsResult = results[0] || { hits: [] }
  const privateOfferingsResult = results[1] || { hits: [] }

  const groupHits = (groupEventsResult.hits || []).map(normalizeHitPersonas).map(normalizeOfferingHit)
  const privateHits = (privateOfferingsResult.hits || [])
    .map((hit) => ({ format: 'private', ...hit }))
    .map(normalizeHitPersonas)
    .map(normalizeOfferingHit)
    .flatMap(expandByFacilities)

  const byObjectId = [...groupHits, ...privateHits].reduce((acc, hit) => {
    const key = String(hit.objectID)
    if (!acc.has(key)) acc.set(key, hit)
    return acc
  }, new Map())
  const mergedInSaveOrder = idsInSaveOrder
    .map((id) => byObjectId.get(String(id)))
    .filter(Boolean)

  const sortedAll = sortOfferings(mergedInSaveOrder, normalizedState.sort)
  const loadedHits = sortedAll.slice(0, hitsPerPageCumulative)
  const totalFound = sortedAll.length
  const hasMore = totalFound > hitsPerPageCumulative

  return {
    hits: loadedHits,
    allHits: loadedHits,
    unfilteredHits: sortedAll,
    totalHits: totalFound,
    totalPages: Math.max(Math.ceil(totalFound / perPage), 1),
    page,
    perPage,
    hasMore,
    nextPage: hasMore ? page + 1 : null,
    coachHits: [],
    hasOfferingsInAreaIgnoringPersona: totalFound > 0,
  }
}

export const searchPgaCoachOfferings = async (searchState = {}) => {
  const normalizedState = normalizeJourneySearchState(searchState)
  if (normalizedState.savedOfferingsOnly) {
    return searchPgaCoachOfferingsSavedOnly(searchState, normalizedState)
  }
  const requestedPage = parseNumber(normalizedState.page) || 1
  const page = Math.max(requestedPage, 1)
  const perPage = parseNumber(normalizedState.perPage) || JOURNEY_OFFERINGS_PAGE_SIZE
  const hitsPerPage = page * perPage
  const aroundRadius = getJourneyDistanceMeters(normalizedState.distance)
  const geoParams = normalizedState.aroundLatLng
    ? { aroundLatLng: normalizedState.aroundLatLng }
    : { aroundLatLngViaIP: true }
  const sharedOfferingParams = {
    hitsPerPage,
    getRankingInfo: true,
    clickAnalytics: true,
    aroundRadius,
    ...geoParams,
  }

  const privateCategories = parseArray(normalizedState.privateCategories)
  const privateFilter = buildPrivateOfferingsSearchFilters(privateCategories)
  const personaFilter = buildPersonaFilter(normalizedState.persona)
  const priceFilter = buildPriceFilter(normalizedState.price)
  const availabilityWindowFilter = buildAvailabilityWindowFilter(normalizedState.schedule)
  const groupEventsTimeWindowFilter = buildGroupEventsTimeWindowFilter(normalizedState.schedule)
  const groupEventsFormatTagFilter = buildGroupEventsFormatTagFilter(normalizedState.formats)

  const groupEventsFilters = [
    buildGroupEventsSearchFilters(undefined, { includeFullEvents: true }),
    groupEventsFormatTagFilter,
    personaFilter,
    priceFilter,
    groupEventsTimeWindowFilter,
  ].filter(Boolean).join(' AND ')
  const privateOfferingsFilters = [privateFilter, personaFilter, priceFilter, availabilityWindowFilter].filter(Boolean).join(' AND ') || null

  const groupEventsFiltersNoPersona = [
    buildGroupEventsSearchFilters(undefined, { includeFullEvents: true }),
    groupEventsFormatTagFilter,
    priceFilter,
    groupEventsTimeWindowFilter,
  ].filter(Boolean).join(' AND ')
  const privateOfferingsFiltersNoPersona = [privateFilter, priceFilter, availabilityWindowFilter].filter(Boolean).join(' AND ') || null

  const personaProbeHitsPerPage = 1
  const personaProbeParams = {
    hitsPerPage: personaProbeHitsPerPage,
    getRankingInfo: true,
    clickAnalytics: true,
    aroundRadius,
    ...geoParams,
  }

  const searchQueries = [
    {
      indexName: process.env.ALGOLIA_GROUP_EVENTS_INDEX_NAME,
      query: normalizedState.query || '',
      params: {
        ...sharedOfferingParams,
        filters: groupEventsFilters,
      },
    },
    {
      indexName: process.env.ALGOLIA_PRIVATE_OFFERINGS_INDEX_NAME,
      query: normalizedState.query || '',
      params: {
        ...sharedOfferingParams,
        ...(privateOfferingsFilters ? { filters: privateOfferingsFilters } : {}),
      },
    },
    {
      indexName: process.env.ALGOLIA_COACH_INDEX_NAME,
      query: normalizedState.placeName || '',
      params: {
        hitsPerPage: EXTERNALLY_BOOKABLE_COACHES_PAGE_SIZE,
        attributesToRetrieve: COACH_ATTRIBUTES_TO_RETRIEVE,
        filters: 'isExternallyBookable:true',
        getRankingInfo: true,
        aroundRadius,
        ...geoParams,
      },
    },
  ]

  if (normalizedState.persona) {
    searchQueries.push(
      {
        indexName: process.env.ALGOLIA_GROUP_EVENTS_INDEX_NAME,
        query: normalizedState.query || '',
        params: {
          ...personaProbeParams,
          filters: groupEventsFiltersNoPersona,
        },
      },
      {
        indexName: process.env.ALGOLIA_PRIVATE_OFFERINGS_INDEX_NAME,
        query: normalizedState.query || '',
        params: {
          ...personaProbeParams,
          ...(privateOfferingsFiltersNoPersona ? { filters: privateOfferingsFiltersNoPersona } : {}),
        },
      },
    )
  }

  const { results } = await searchClient.search(searchQueries)

  const groupEventsResult = results[0] || { hits: [], nbHits: 0 }
  const privateOfferingsResult = results[1] || { hits: [], nbHits: 0 }
  const coachHits = results[2]?.hits || []
  const groupPersonaProbe = normalizedState.persona ? (results[3] || { nbHits: 0 }) : null
  const privatePersonaProbe = normalizedState.persona ? (results[4] || { nbHits: 0 }) : null

  const groupHits = normalizeEligibleGroupEventHits(groupEventsResult.hits)
  const privateHits = (privateOfferingsResult.hits || [])
    .map((hit) => ({ format: 'private', ...hit }))
    .map(normalizeHitPersonas)
    .map(normalizeOfferingHit)
    .flatMap(expandByFacilities)
  const mergedHits = [...groupHits, ...privateHits]

  const hasOfferingsInAreaIgnoringPersona = normalizedState.persona
    ? Number(groupPersonaProbe?.nbHits || 0) + Number(privatePersonaProbe?.nbHits || 0) > 0
    : mergedHits.length > 0

  const filteredHits = sortOfferings(
    filterOfferings(mergedHits, normalizedState),
    normalizedState.sort,
  )
  const loadedHits = filteredHits.slice(0, hitsPerPage)
  const totalNbHits = Number(groupEventsResult.nbHits || 0) + Number(privateOfferingsResult.nbHits || 0)
  const hasMore = filteredHits.length > hitsPerPage || totalNbHits > hitsPerPage
  return {
    hits: loadedHits,
    allHits: loadedHits,
    unfilteredHits: sortOfferings(mergedHits, normalizedState.sort),
    totalHits: loadedHits.length,
    totalPages: Math.max(Math.ceil(loadedHits.length / perPage), 1),
    page,
    perPage,
    hasMore,
    nextPage: hasMore ? page + 1 : null,
    coachHits,
    hasOfferingsInAreaIgnoringPersona,
  }
}

/** Browser-only cache so tab navigations between profile sections don't refetch + skeleton. */
const coachProfileOfferingsCache = new Map()

export const getCachedCoachProfileOfferings = (coachId) => {
  if (!isBrowser() || !coachId) return undefined
  return coachProfileOfferingsCache.has(coachId)
    ? coachProfileOfferingsCache.get(coachId)
    : undefined
}

export const setCachedCoachProfileOfferings = (coachId, offeringsByPersona) => {
  if (!isBrowser() || !coachId) return
  coachProfileOfferingsCache.set(coachId, offeringsByPersona)
}

/** @internal test helper */
export const resetCoachProfileOfferingsCacheForTests = () => {
  coachProfileOfferingsCache.clear()
}

export const searchCoachProfileOfferings = async (coachId) => {
  if (!coachId) return { offeringsByPersona: {} }

  const { results } = await searchClient.search([
    {
      indexName: process.env.ALGOLIA_GROUP_EVENTS_INDEX_NAME,
      query: '',
      params: {
        filters: `coach_id:${coachId} AND ${buildGroupEventsSearchFilters(undefined, { includeFullEvents: true })}`,
        hitsPerPage: 100,
      },
    },
    {
      indexName: process.env.ALGOLIA_PRIVATE_OFFERINGS_INDEX_NAME,
      query: '',
      params: {
        filters: buildPrivateOfferingsCoachIdFilter(coachId),
        hitsPerPage: 100,
      },
    },
  ])

  const groupEventsResult = results[0] || { hits: [] }
  const privateOfferingsResult = results[1] || { hits: [] }

  const groupHits = normalizeEligibleGroupEventHits(groupEventsResult.hits)

  const privateHits = (privateOfferingsResult.hits || [])
    .map((hit) => ({ format: 'private', ...hit }))
    .map(normalizeHitPersonas)
    .map(normalizeOfferingHit)
    .flatMap(expandByFacilities)

  const offeringsByPersona = {};
  [...groupHits, ...privateHits].forEach((offering) => {
    const personaIds = parseArray(offering.personaTags)
      .map((id) => PGA_COACH_PERSONA_ALIAS_MAP[id] || id)
      .filter((id) => PGA_COACH_PERSONA_MAP[id])
    const keys = personaIds.length ? [...personaIds] : [COACH_PROFILE_UNTAGGED_OFFERINGS_KEY]
    // Jr. League often ships with empty `personas`. Keep `_untagged` for About
    // flatten, and also fill `juniors` so leftover Juniors shop lists still merge.
    if (!personaIds.length && getCoachProfileOfferingServiceId(offering) === 'jrl') {
      keys.push('juniors')
    }
    keys.forEach((personaId) => {
      if (!offeringsByPersona[personaId]) offeringsByPersona[personaId] = []
      offeringsByPersona[personaId].push(offering)
    })
  })

  setCachedCoachProfileOfferings(coachId, offeringsByPersona)
  return { offeringsByPersona }
}
